import React, { useEffect, useState, useRef } from "react";
import { api } from "@/lib/api";
import { Plane, X, Globe2 } from "lucide-react";

/**
 * Airport IATA autocomplete with dropdown.
 * Props: value (string IATA), onChange(airportObj), label, placeholder, testId
 */
export default function AirportAutocomplete({ value, onChange, label, placeholder, testId }) {
  const [query, setQuery] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [selected, setSelected] = useState(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    let active = true;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/catalog/airports/search`, { params: { q: query || "", limit: 25 } });
        if (active) setOptions(data);
      } catch (_e) {}
    }, 120);
    return () => { active = false; clearTimeout(t); };
  }, [query]);

  useEffect(() => {
    const onClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Sync external value (e.g. coming from search params)
  useEffect(() => {
    if (value && value !== selected?.code) {
      (async () => {
        try {
          const { data } = await api.get(`/catalog/airports/search`, { params: { q: value } });
          const exact = data.find((a) => a.code.toUpperCase() === String(value).toUpperCase());
          if (exact) { setSelected(exact); setQuery(`${exact.city} (${exact.code})`); }
          else setQuery(value);
        } catch (_e) {}
      })();
    }
  }, [value]); // eslint-disable-line

  const pick = (a) => {
    setSelected(a);
    setQuery(`${a.city} (${a.code})`);
    setOpen(false);
    onChange?.(a);
  };

  const clear = () => {
    setSelected(null); setQuery(""); setOpen(true);
    onChange?.(null);
  };

  return (
    <div ref={wrapRef} className="relative">
      {label && <label className="text-xs text-slate-500 block mb-1">{label}</label>}
      <div className="relative">
        <input
          data-testid={testId}
          className="w-full h-11 rounded-md border border-slate-200 bg-white px-3 pr-9 text-sm font-medium text-[var(--brand-accent)] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[var(--brand-primary)]/40"
          placeholder={placeholder || "Airport"}
          value={query}
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); setSelected(null); }}
        />
        {query && (
          <button type="button" onClick={clear} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
      {open && options.length > 0 && (
        <div className="absolute z-50 mt-1 w-full max-h-80 overflow-auto bg-white border border-slate-200 rounded-xl shadow-xl">
          {options.map((a) => (
            <button
              type="button"
              key={a.code + (a.is_metro ? "-metro" : "")}
              data-testid={`${testId}-option-${a.code}`}
              onClick={() => pick(a)}
              className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-teal-50 text-left ${a.is_metro ? "bg-amber-50/40 border-b border-amber-200" : ""}`}
            >
              <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${a.is_metro ? "bg-amber-100 text-amber-700" : "bg-teal-50 text-[var(--brand-primary)]"}`}>
                {a.is_metro ? <Globe2 className="w-4 h-4" /> : <Plane className="w-4 h-4" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[var(--brand-accent)] truncate">
                  {a.city} <span className="text-slate-400 font-normal">· {a.country}</span>
                  {a.is_metro && <span className="ml-2 text-[10px] uppercase tracking-wider bg-amber-200 text-amber-900 px-1.5 py-0.5 rounded">All airports</span>}
                </div>
                <div className="text-xs text-slate-500 truncate">{a.name}</div>
              </div>
              <span className={`text-xs font-mono font-bold px-2 py-0.5 rounded ${a.is_metro ? "bg-amber-100 text-amber-900" : "bg-slate-100 text-slate-700"}`}>{a.code}</span>
            </button>
          ))}
        </div>
      )}
      {open && options.length === 0 && query && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-xl px-3 py-3 text-sm text-slate-500">
          No airports match "{query}".
        </div>
      )}
    </div>
  );
}
